/**
 * buildOrgTheme / extractCssVarsFromPalette
 *
 * Uses MUI Joy's own extendTheme() to build a complete theme from an org's
 * branding config.  extendTheme() derives ALL variant tokens (softBg, solidBg,
 * plainHoverBg, outlinedBorder, *Channel rgba values, etc.) from the provided
 * palette shades — so the admin only has to specify a small set of colours and
 * everything else cascades automatically.
 *
 * We then extract every CSS variable from the resolved palette and inject them
 * via a <style> tag using html[data-joy-color-scheme] selectors (specificity
 * 0,1,1), which beats the outer CssVarsProvider's [data-joy-color-scheme]
 * (0,1,0).  No CssVarsProvider restructuring needed.
 */

import { extendTheme } from "@mui/joy/styles";
import type { OrgThemeConfig, OrgThemeMode } from "../common/types";
import { sharedThemeOptions } from "./themes/defaultTheme";
import { theme as baseTheme } from "./themes";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SHADE_KEYS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/** Pick only the raw shade values (50–900) from a resolved MUI Joy palette
 *  colour object.  Drops computed variant tokens so extendTheme() re-derives
 *  them cleanly from the new shades. */
function pickShades(src: unknown): Record<number, string> | undefined {
  if (!src || typeof src !== "object") return undefined;
  const result: Record<number, string> = {};
  for (const k of SHADE_KEYS) {
    const v = (src as Record<number, unknown>)[k];
    if (typeof v === "string") result[k] = v;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function buildModeColorScheme(
  mode: OrgThemeMode | undefined,
  modeKey: "light" | "dark",
): { palette: Record<string, unknown> } {
  const basePal = (baseTheme as any).colorSchemes?.[modeKey]?.palette ?? {};

  const palette: Record<string, unknown> = {};

  // Palette shades — extendTheme() derives all variant tokens from these
  for (const key of ["primary", "neutral", "success", "warning", "danger"] as const) {
    const orgShades = mode?.palette?.[key];
    const baseShades = pickShades(basePal[key]);
    const resolved = orgShades ?? baseShades;
    if (resolved) palette[key] = resolved;
  }

  // Backgrounds
  const baseBg: Record<string, string> = basePal.background ?? {};
  const bg: Record<string, string> = {};
  const addBg = (k: string, org: string | undefined) => {
    const v = org ?? baseBg[k];
    if (v) bg[k] = v;
  };
  addBg("body",    mode?.backgrounds?.body);
  addBg("surface", mode?.backgrounds?.surface);
  addBg("level1",  mode?.levels?.level1);
  addBg("level2",  mode?.levels?.level2);
  addBg("level3",  mode?.levels?.level3);
  if (Object.keys(bg).length) palette.background = bg;

  // Text
  const baseText: Record<string, string> = basePal.text ?? {};
  const text: Record<string, string> = {};
  const addText = (k: string, org: string | undefined) => {
    const v = org ?? baseText[k];
    if (v) text[k] = v;
  };
  addText("primary",   mode?.colors?.text_primary);
  addText("secondary", mode?.colors?.text_secondary);
  addText("tertiary",  undefined); // not org-editable; preserve base
  if (Object.keys(text).length) palette.text = text;

  // Divider
  const divider = mode?.colors?.divider ?? basePal.divider;
  if (divider) palette.divider = divider;

  return { palette };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Build a complete MUI Joy theme for the given org config.
 *  All variant tokens (softBg, plainHoverBg, …) are derived automatically. */
export function buildOrgTheme(config: OrgThemeConfig) {
  return extendTheme({
    colorSchemes: {
      light: buildModeColorScheme(config.light, "light"),
      dark:  buildModeColorScheme(config.dark, "dark"),
    },
    ...sharedThemeOptions,
  });
}

/** Extract every CSS variable from a resolved MUI Joy palette colour scheme
 *  into a flat { "--joy-palette-...": value } record. */
export function extractCssVars(palette: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!palette || typeof palette !== "object") return out;
  const pal = palette as Record<string, unknown>;

  // Colour roles: primary, neutral, success, warning, danger
  for (const colorKey of ["primary", "neutral", "success", "warning", "danger"]) {
    const color = pal[colorKey];
    if (!color || typeof color !== "object") continue;
    for (const [k, v] of Object.entries(color as object)) {
      if (typeof v === "string" && v) {
        out[`--joy-palette-${colorKey}-${k}`] = v;
      }
    }
  }

  // Background
  if (pal.background && typeof pal.background === "object") {
    for (const [k, v] of Object.entries(pal.background as object)) {
      if (typeof v === "string" && v) {
        out[`--joy-palette-background-${k}`] = v;
      }
    }
  }

  // Text
  if (pal.text && typeof pal.text === "object") {
    for (const [k, v] of Object.entries(pal.text as object)) {
      if (typeof v === "string" && v) {
        out[`--joy-palette-text-${k}`] = v;
      }
    }
  }

  // Divider
  if (typeof pal.divider === "string" && pal.divider) {
    out["--joy-palette-divider"] = pal.divider;
  }

  return out;
}

/** Build a CSS string that injects all palette vars for both colour schemes,
 *  scoped to `selector` (e.g. "html" or ".preview-scope"). */
export function buildCssVarBlock(
  orgTheme: ReturnType<typeof buildOrgTheme>,
  selector: string,
): string {
  const blocks: string[] = [];

  for (const scheme of ["light", "dark"] as const) {
    const palette = (orgTheme as any).colorSchemes?.[scheme]?.palette;
    if (!palette) continue;
    const vars = extractCssVars(palette);
    const lines = Object.entries(vars).map(([k, v]) => `  ${k}: ${v};`);
    if (lines.length) {
      blocks.push(`${selector}[data-joy-color-scheme="${scheme}"] {\n${lines.join("\n")}\n}`);
    }
  }

  return blocks.join("\n");
}
